import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, currency } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Download, Package } from "lucide-react";

export function Profile() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [wishlist, setWishlist] = useState({ products: [], experts: [], partners: [] });

  useEffect(() => {
    if (user && user !== false) {
      api.get("/orders").then(r => setOrders(r.data));
      api.get("/me/entitlements").then(r => setEntitlements(r.data));
      api.get("/wishlist").then(r => setWishlist(r.data));
    }
  }, [user]);

  const handleDownload = async (ent) => {
    try {
      const { data } = await api.get(`/entitlements/${ent.id}/download`);
      // If backend returned a file_id path, prepend backend base
      const url = data.url?.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL}${data.url}`
        : data.url;
      if (url) window.open(url, "_blank", "noopener");
    } catch {}
  };

  if (!user) return null;
  if (user === false) return <div className="max-w-md mx-auto py-20 text-center"><Link to="/login" className="tl-btn-primary">Login</Link></div>;

  return (
    <div data-testid="customer-profile" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="tl-label mb-1">Profile</div>
        <h1 className="text-3xl font-display font-bold">{user.name}</h1>
        <div className="text-sm text-[#475569]">{user.email}</div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto no-scrollbar">
        {[["orders","Orders"],["downloads","My Downloads"],["wishlist","Wishlist"]].map(([k, l]) => (
          <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-3 text-sm border-b-2 shrink-0 ${tab === k ? "border-[#0891B2]" : "border-transparent text-[#475569]"}`}>{l}</button>
        ))}
      </div>

      {tab === "orders" && (orders.length === 0 ? <Empty msg="No orders yet." /> : (
        <div className="space-y-3">{orders.map(o => <OrderCard key={o.id} order={o} />)}</div>
      ))}

      {tab === "downloads" && (
        <div className="space-y-2">
          {entitlements.length === 0 ? <Empty msg="No digital downloads yet." /> :
            entitlements.map((ent) => (
              <div key={ent.id} className="tl-card p-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  {ent.product_image && <img src={ent.product_image} alt="" className="w-12 h-12 rounded object-cover" />}
                  <div>
                    <div className="font-semibold text-sm">{ent.product_name}</div>
                    <div className="text-xs text-[#475569]">Granted {new Date(ent.granted_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <button data-testid={`download-${ent.product_id}`} onClick={() => handleDownload(ent)} className="tl-btn-ghost text-sm inline-flex items-center gap-1">
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>
            ))}
        </div>
      )}

      {tab === "wishlist" && (
        <div>
          <div className="text-sm text-[#475569] mb-3">Saved: {wishlist.products?.length || 0} products · {wishlist.experts?.length || 0} experts · {wishlist.partners?.length || 0} partners</div>
          {(!wishlist.products?.length && !wishlist.experts?.length && !wishlist.partners?.length) && <Empty msg="Your wishlist is empty." />}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order }) {
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    if (!detail) {
      const { data } = await api.get(`/orders/${order.id}`);
      setDetail(data);
    }
    setOpen(!open);
  };
  return (
    <div className="tl-card p-4">
      <div className="flex justify-between items-center">
        <div>
          <div className="font-mono text-xs text-[#475569]">#{order.id.slice(0, 8)}</div>
          <div className="text-sm">{new Date(order.created_at).toLocaleString()}</div>
          <div className="text-xs text-[#475569] mt-1">
            {order.has_physical && order.has_digital ? "Mixed" : order.has_physical ? "Physical" : "Digital"} · {order.vendor_ids?.length || 1} vendor{(order.vendor_ids?.length || 1) > 1 ? "s" : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-bold">{currency(order.total)}</div>
          <span className="tl-chip">{order.status}</span>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {order.items?.map((it, i) => (
          <div key={i} className="text-sm text-[#475569] flex justify-between">
            <span>• {it.name} × {it.quantity}</span>
            <span className="text-xs">{it.product_type === "digital" ? "Digital" : "Physical"}</span>
          </div>
        ))}
      </div>
      <button onClick={toggle} className="text-xs text-[#0891B2] mt-3">
        {open ? "Hide" : "View"} vendor breakdown
      </button>
      {open && detail && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
          {detail.shipping_address && (
            <div data-testid={`order-address-${order.id}`} className="text-xs text-[#475569]">
              Ship to: {detail.shipping_address.full_name}, {detail.shipping_address.line1}, {detail.shipping_address.city} – {detail.shipping_address.pincode}
            </div>
          )}
          {(detail.seller_orders || []).map((so) => (
            <div key={so.id} className="bg-[#FFFFFF] rounded p-3 text-sm">
              <div className="flex justify-between mb-1">
                <div className="flex items-center gap-2"><Package className="w-3.5 h-3.5" /><span className="font-medium">{so.vendor_name || so.vendor_id.slice(0, 8)}</span></div>
                <span className="tl-chip text-[10px]">{so.status}</span>
              </div>
              <div className="text-xs text-[#475569]">{so.items?.length} item(s) · {currency(so.subtotal)} · {so.order_type}</div>
              {so.shipping?.tracking_id && <div className="text-xs text-[#0891B2] mt-1">Tracking: {so.shipping.tracking_id}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="tl-card p-8 text-center text-[#475569]">{msg}</div>;
}

export function StaticPage({ title, children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="text-3xl font-display font-bold mb-6">{title}</h1>
      <div className="text-[#475569] space-y-4 leading-relaxed">{children}</div>
    </div>
  );
}
